// Table de reference des allures, portee depuis l'onglet "Allures" du
// classeur feuille/Allures_repetitions.xlsx (plage B9:Q15) qui alimente
// feuille/Code.gs.
//
// Ces parametres sont desormais des preferences utilisateur, stockees
// cote serveur (table user_allure_zone, GET/PUT/DELETE
// /api/preference/allures) et chargees via useAllureTable(). ALLURE_TABLE
// ci-dessous reste la table PAR DEFAUT : utilisee tant que l'utilisateur
// n'a rien personnalise, ou si le serveur est injoignable. Elle doit rester
// identique a AllureZoneDefaults.java cote serveur.
export type ZoneRow = {
  nom: string;
  pctVMA: number;
  code: string;
  ratioMin: number;
  ratioMax: number;
  repsMin: number;
  repsMax: number;
  volMin: number;
  volMax: number;
  distMin: number;
  distMax: number;
};

export const ALLURE_TABLE: ZoneRow[] = [
  { nom: 'VMA', pctVMA: 1.00, code: 'VMA', ratioMin: 0.50, ratioMax: 1.00, repsMin: 8, repsMax: 15, volMin: 2000, volMax: 3000, distMin: 200, distMax: 600 },
  { nom: 'Allure 3 km', pctVMA: 0.96, code: '3K', ratioMin: 0.50, ratioMax: 0.85, repsMin: 5, repsMax: 10, volMin: 3000, volMax: 5000, distMin: 400, distMax: 1000 },
  { nom: 'Allure 5 km', pctVMA: 0.91, code: '5K', ratioMin: 0.30, ratioMax: 0.60, repsMin: 4, repsMax: 8, volMin: 3000, volMax: 5000, distMin: 600, distMax: 1600 },
  { nom: 'Allure 10 km', pctVMA: 0.86, code: '10K', ratioMin: 0.20, ratioMax: 0.35, repsMin: 3, repsMax: 6, volMin: 5000, volMax: 8000, distMin: 800, distMax: 3000 },
  { nom: 'Allure semi', pctVMA: 0.83, code: 'HP', ratioMin: 0.15, ratioMax: 0.20, repsMin: 2, repsMax: 4, volMin: 6000, volMax: 10000, distMin: 800, distMax: 5000 },
  { nom: 'Allure marathon', pctVMA: 0.78, code: 'MP', ratioMin: 0.10, ratioMax: 0.15, repsMin: 2, repsMax: 4, volMin: 8000, volMax: 12000, distMin: 1000, distMax: 10000 },
  { nom: 'Steady (Soutenu)', pctVMA: 0.75, code: 'STEADY', ratioMin: 0.0, ratioMax: 0.0, repsMin: 1, repsMax: 1, volMin: 3000, volMax: 12000, distMin: 3000, distMax: 12000 },
  { nom: 'Easy (Facile)', pctVMA: 0.70, code: 'EASY', ratioMin: 0.0, ratioMax: 0.0, repsMin: 1, repsMax: 1, volMin: 4500, volMax: 15000, distMin: 4500, distMax: 15000 },
  { nom: 'Slow (Lente)', pctVMA: 0.63, code: 'RECUP', ratioMin: NaN, ratioMax: NaN, repsMin: NaN, repsMax: NaN, volMin: NaN, volMax: NaN, distMin: NaN, distMax: NaN },
];

// Format d'echange avec /api/preference/allures : identique a ZoneRow, sauf
// que les bornes "non configurees" (NaN ici) y valent null (JSON n'a pas
// de NaN).
export type ZoneRowApi = {
  [K in keyof ZoneRow]: ZoneRow[K] extends number ? number | null : ZoneRow[K];
};

const CHAMPS_NUMERIQUES: (keyof ZoneRow)[] = [
  'pctVMA', 'ratioMin', 'ratioMax', 'repsMin', 'repsMax', 'volMin', 'volMax', 'distMin', 'distMax',
];

export function zoneDepuisApi(z: ZoneRowApi): ZoneRow {
  const zone = { ...z } as unknown as Record<string, unknown>;
  CHAMPS_NUMERIQUES.forEach((champ) => {
    const v = zone[champ];
    zone[champ] = v === null || v === undefined ? NaN : Number(v);
  });
  return zone as unknown as ZoneRow;
}

export function zoneVersApi(z: ZoneRow): ZoneRowApi {
  const zone = { ...z } as unknown as Record<string, unknown>;
  CHAMPS_NUMERIQUES.forEach((champ) => {
    const v = zone[champ] as number;
    zone[champ] = typeof v === 'number' && isFinite(v) ? v : null;
  });
  return zone as unknown as ZoneRowApi;
}

// Alias acceptes en saisie pour chaque code canonique de la table (voir
// Code.gs / ALLURE_ALIASES).
//
// STEADY ("Steady (Soutenu)", 75 % VMA) et EASY ("Easy (Facile)", 70 %)
// sont deux zones distinctes ; STEADY etait auparavant un simple alias
// d'EASY. La zone RECUP porte l'allure "Slow (Lente)" (63 %), d'ou les
// alias SLOW / LENTE.
//
// RECUP n'est utilise que comme marqueur de recuperation (ex: "R3'30''"
// ou "3'30''@RECUP" a la suite d'un effort) : sa distance parcourue reste
// toujours estimee a l'allure EASY (voir calculerDistanceRecuperation),
// jamais a la sienne propre. Mais Code.gs (cf commentaire sur
// obtenirPlageTableAllures) accepte aussi RECUP comme allure cible d'un
// bloc a part entiere dans une composition "+" (ex:
// "6x(1'@5K-R1'15'')+3'@RECUP" pour un jogging de recuperation entre
// deux series) : trouverZone() y resout alors ce bloc via la ligne RECUP
// de la table ci-dessus, sans check de distance/reps/volume/ratio de
// recup (bornes non configurees = non pertinentes pour un jogging).
export const ALLURE_ALIASES: Record<string, string[]> = {
  VMA: ['VMA'],
  '3K': ['3K', '3KM', 'A3K'],
  '5K': ['5K', '5KM', 'A5K'],
  '10K': ['10K', '10KM', 'A10K'],
  HP: ['SEMI', 'HM', 'HP'],
  MP: ['MP', 'M', 'MARATHON', 'MARA'],
  STEADY: ['STEADY', 'SOUTENU'],
  EASY: ['EASY', 'FACILE'],
  RECUP: ['RECUP', 'RECUPERATION', 'SLOW', 'LENTE'],
};
